import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getDatabase,
  ref,
  push,
  set,
  update,
  get,
  query,
  orderByChild,
  equalTo,
  onValue,
  child,
  serverTimestamp,
} from "firebase/database";
import { RootState } from "../index";
import { app } from "../../config/firebase";

// Initialize Realtime Database
const rtdb = getDatabase(app);

// Types
interface Notification {
  id?: string;
  trigger: string;
  message: string;
  triggeredBy: string;
  createdAt: any;
  read: boolean;
  userId: string;
  userEmail: string;
  meta?: any; // Additional metadata for notifications
}

interface CreateNotificationParams {
  userId: string;
  userEmail: string;
  message: string;
  trigger: string;
  triggeredBy: string;
  meta?: any; // Add support for metadata
}

interface MarkAsReadParams {
  userId: string;
  notificationId: string;
}

interface NotificationState {
  userNotifications: Notification[];
  allNotifications: Notification[];
  loading: boolean;
  error: string | null;
  newNotification: Notification | null; // New field to store the latest notification
}

const initialState: NotificationState = {
  userNotifications: [],
  allNotifications: [],
  loading: false,
  error: null,
  newNotification: null, // Initialize new notification as null
};

// Utility to recursively remove undefined values from an object (including nested)
function removeUndefined(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  } else if (obj && typeof obj === "object") {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = removeUndefined(value);
      }
      return acc;
    }, {} as any);
  }
  return obj;
}

// Async Thunks
export const createNotification = createAsyncThunk(
  "notifications/create",
  async (
    {
      userId,
      userEmail,
      message,
      trigger,
      triggeredBy,
      meta = {}, // Default to empty object if not provided
    }: CreateNotificationParams,
    { rejectWithValue }
  ) => {
    try {
      console.log("Creating notification with data:", {
        userId,
        userEmail,
        message,
        trigger,
        triggeredBy,
        meta,
      });

      if (!userId) {
        console.error(
          "Cannot create notification: userId is empty or undefined"
        );
        return rejectWithValue("User ID is required to create a notification");
      }

      let notificationData = {
        trigger,
        message,
        triggeredBy,
        createdAt: serverTimestamp(),
        read: false,
        userId,
        userEmail,
        meta: meta || {}, // Ensure meta is never undefined
      };

      // Remove undefined values (including nested in meta)
      notificationData = removeUndefined(notificationData);

      // 1. Add to userNotifications
      const userNotifRef = ref(
        rtdb,
        `userNotifications/${userId}/notifications`
      );
      const newUserNotifRef = push(userNotifRef);
      console.log(
        "Adding notification to user notifications:",
        userNotifRef.toString()
      );
      await set(newUserNotifRef, notificationData);
      console.log("Successfully added notification to user notifications");

      // 2. Add to global admin view (allNotifications)
      const globalNotifRef = ref(rtdb, "allNotifications");
      const newGlobalNotifRef = push(globalNotifRef);
      console.log(
        "Adding notification to global notifications:",
        globalNotifRef.toString()
      );
      await set(newGlobalNotifRef, notificationData);
      console.log("Successfully added notification to global notifications");

      return {
        userNotificationId: newUserNotifRef.key,
        globalNotificationId: newGlobalNotifRef.key,
        ...notificationData,
      };
    } catch (error) {
      console.error("Failed to create notification:", error);
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to create notification"
      );
    }
  }
);

export const getUserNotifications = createAsyncThunk(
  "notifications/getUserNotifications",
  async (userId: string) => {
    // If userId is 'all', fetch all notifications for admin view
    if (userId === "all") {
      const notifRef = ref(rtdb, "allNotifications");
      const notifQuery = query(notifRef, orderByChild("createdAt"));

      return new Promise<Notification[]>((resolve, reject) => {
        onValue(
          notifQuery,
          (snapshot) => {
            const notifications: Notification[] = [];
            snapshot.forEach((childSnapshot) => {
              notifications.push({
                id: childSnapshot.key,
                ...childSnapshot.val(),
              });
            });
            // Sort by createdAt in descending order
            notifications.sort((a, b) => {
              if (!a.createdAt || !b.createdAt) return 0;
              return b.createdAt - a.createdAt;
            });
            resolve(notifications);
          },
          (error) => {
            reject(error);
          }
        );
      });
    } else {
      // Regular user notifications
      const userNotifRef = ref(
        rtdb,
        `userNotifications/${userId}/notifications`
      );
      const notifQuery = query(userNotifRef, orderByChild("createdAt"));

      return new Promise<Notification[]>((resolve, reject) => {
        onValue(
          notifQuery,
          (snapshot) => {
            const notifications: Notification[] = [];
            snapshot.forEach((childSnapshot) => {
              notifications.push({
                id: childSnapshot.key,
                ...childSnapshot.val(),
              });
            });
            // Sort by createdAt in descending order
            notifications.sort((a, b) => {
              if (!a.createdAt || !b.createdAt) return 0;
              return b.createdAt - a.createdAt;
            });
            resolve(notifications);
          },
          (error) => {
            reject(error);
          }
        );
      });
    }
  }
);

export const getAllNotifications = createAsyncThunk(
  "notifications/getAllNotifications",
  async () => {
    const notifRef = ref(rtdb, "allNotifications");
    const notifQuery = query(notifRef, orderByChild("createdAt"));

    return new Promise<Notification[]>((resolve, reject) => {
      onValue(
        notifQuery,
        (snapshot) => {
          const notifications: Notification[] = [];
          snapshot.forEach((childSnapshot) => {
            notifications.push({
              id: childSnapshot.key,
              ...childSnapshot.val(),
            });
          });
          // Sort by createdAt in descending order
          notifications.sort((a, b) => {
            if (!a.createdAt || !b.createdAt) return 0;
            return b.createdAt - a.createdAt;
          });
          resolve(notifications);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }
);

export const markNotificationAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async ({ userId, notificationId }: MarkAsReadParams) => {
    const notifRef = ref(
      rtdb,
      `userNotifications/${userId}/notifications/${notificationId}`
    );
    await update(notifRef, {
      read: true,
    });
    return { userId, notificationId };
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  "notifications/markAllAsRead",
  async (userId: string) => {
    try {
      // Get all user notifications
      const userNotifRef = ref(
        rtdb,
        `userNotifications/${userId}/notifications`
      );
      const snapshot = await get(userNotifRef);

      if (!snapshot.exists()) {
        return { userId, updatedCount: 0 };
      }

      // Prepare batch update object
      const updates: { [key: string]: any } = {};
      let updateCount = 0;

      snapshot.forEach((childSnapshot) => {
        const notification = childSnapshot.val();
        // Only update unread notifications
        if (!notification.read) {
          updates[
            `userNotifications/${userId}/notifications/${childSnapshot.key}/read`
          ] = true;
          updateCount++;
        }
      });

      // Perform batch update if there are notifications to update
      if (updateCount > 0) {
        await update(ref(rtdb), updates);
      }

      return { userId, updatedCount: updateCount };
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      throw error;
    }
  }
);

export const markAllGlobalNotificationsAsRead = createAsyncThunk(
  "notifications/markAllGlobalAsRead",
  async () => {
    try {
      // Get all global notifications
      const globalNotifRef = ref(rtdb, "allNotifications");
      const snapshot = await get(globalNotifRef);

      if (!snapshot.exists()) {
        return { updatedCount: 0 };
      }

      // Prepare batch update object
      const updates: { [key: string]: any } = {};
      let updateCount = 0;

      snapshot.forEach((childSnapshot) => {
        const notification = childSnapshot.val();
        // Only update unread notifications
        if (!notification.read) {
          updates[`allNotifications/${childSnapshot.key}/read`] = true;
          updateCount++;
        }
      });

      // Perform batch update if there are notifications to update
      if (updateCount > 0) {
        await update(ref(rtdb), updates);
      }

      return { updatedCount: updateCount };
    } catch (error) {
      console.error("Failed to mark all global notifications as read:", error);
      throw error;
    }
  }
);

export const markGlobalNotificationAsRead = createAsyncThunk(
  "notifications/markGlobalAsRead",
  async (notificationId: string) => {
    const notifRef = ref(rtdb, `allNotifications/${notificationId}`);
    await update(notifRef, {
      read: true,
    });
    return { notificationId };
  }
);

// New thunk to subscribe to real-time notifications
export const subscribeToUserNotifications = createAsyncThunk(
  "notifications/subscribe",
  async (userId: string, { dispatch }) => {
    try {
      if (!userId) {
        console.error(
          "Cannot subscribe to notifications: userId is empty or undefined"
        );
        return;
      }

      // Reference to the user's notifications
      const userNotifRef = ref(
        rtdb,
        `userNotifications/${userId}/notifications`
      );

      // Create a query ordered by creation time
      const notifQuery = query(userNotifRef, orderByChild("createdAt"));

      // Set up real-time listener
      const unsubscribe = onValue(notifQuery, (snapshot) => {
        // Process all notifications
        const notifications: Notification[] = [];
        snapshot.forEach((childSnapshot) => {
          notifications.push({
            id: childSnapshot.key,
            ...childSnapshot.val(),
          });
        });

        // Sort by createdAt in descending order
        notifications.sort((a, b) => {
          if (!a.createdAt || !b.createdAt) return 0;
          return b.createdAt - a.createdAt;
        });

        // Update user notifications in the store
        dispatch({
          type: "notifications/getUserNotifications/fulfilled",
          payload: notifications,
        });

        // If there are notifications and the newest one is unread, set it as new notification
        if (notifications.length > 0) {
          const latestNotification = notifications[0];
          if (!latestNotification.read) {
            dispatch(setNewNotification(latestNotification));
          }
        }
      });

      // Return the unsubscribe function
      return unsubscribe;
    } catch (error) {
      console.error("Failed to subscribe to notifications:", error);
      throw error;
    }
  }
);

// Slice
const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    clearNotifications: (state) => {
      state.userNotifications = [];
      state.allNotifications = [];
    },
    setNewNotification: (state, action: PayloadAction<Notification | null>) => {
      state.newNotification = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Create notification
    builder.addCase(createNotification.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createNotification.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createNotification.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to create notification";
    });

    // Get user notifications
    builder.addCase(getUserNotifications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      getUserNotifications.fulfilled,
      (state, action: PayloadAction<Notification[]>) => {
        state.loading = false;
        state.userNotifications = action.payload;
      }
    );
    builder.addCase(getUserNotifications.rejected, (state, action) => {
      state.loading = false;
      state.error =
        action.error.message || "Failed to fetch user notifications";
    });

    // Get all notifications
    builder.addCase(getAllNotifications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      getAllNotifications.fulfilled,
      (state, action: PayloadAction<Notification[]>) => {
        state.loading = false;
        state.allNotifications = action.payload;
      }
    );
    builder.addCase(getAllNotifications.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch all notifications";
    });

    // Mark notification as read
    builder.addCase(markNotificationAsRead.fulfilled, (state, action) => {
      const { notificationId } = action.payload;
      const notification = state.userNotifications.find(
        (n) => n.id === notificationId
      );
      if (notification) {
        notification.read = true;
      }
    }); // Mark all notifications as read
    builder.addCase(markAllNotificationsAsRead.fulfilled, (state) => {
      // Update all unread notifications in the state
      state.userNotifications.forEach((notification) => {
        if (!notification.read) {
          notification.read = true;
        }
      });
    });

    // Mark all global notifications as read
    builder.addCase(markAllGlobalNotificationsAsRead.fulfilled, (state) => {
      // Update all unread notifications in the state
      state.allNotifications.forEach((notification) => {
        if (!notification.read) {
          notification.read = true;
        }
      });
    });

    // Mark global notification as read
    builder.addCase(markGlobalNotificationAsRead.fulfilled, (state, action) => {
      const { notificationId } = action.payload;
      const notification = state.allNotifications.find(
        (n) => n.id === notificationId
      );
      if (notification) {
        notification.read = true;
      }
    });
  },
});

// Selectors
export const selectUserNotifications = (state: RootState) =>
  state.notifications.userNotifications;
export const selectAllNotifications = (state: RootState) =>
  state.notifications.allNotifications;
export const selectNotificationsLoading = (state: RootState) =>
  state.notifications.loading;
export const selectNotificationsError = (state: RootState) =>
  state.notifications.error;
export const selectNewNotification = (state: RootState) =>
  state.notifications.newNotification;

// Actions
export const { clearNotifications, setNewNotification } =
  notificationSlice.actions;

// Reducer
export default notificationSlice.reducer;

/*
{
  "rules": {
    "users": {
      "$uid": {
        ".read": "auth != null",
        ".write": "auth != null && auth.uid == $uid"
      }
    },
    "requests": {
      "$expertId": {
        ".read": "auth != null",
        ".write": "auth != null"
      }
    },
    "userNotifications": {
      "$uid": {
        ".read": "auth != null",
        ".write": true
      }
    },
    "allNotifications": {
      ".read": "auth != null",
      ".write": true
    }
  }
}
*/
